// InitialsAvatar — themed placeholder for a missing profile photo (EPD-10138).
//
// Fills a circle with the creator's initials, coloured from portfolio theme
// tokens when present (`--pf-*`) and falling back to app tokens elsewhere, so it
// re-themes live in the portfolio and still looks right on the public
// /portfolio/ page.
// Falls back to a neutral user glyph when there is no name.

import { getInitials } from "@/lib/utils/initials";
import { cn } from "@/lib/utils";
import { User } from "lucide-react";

interface InitialsAvatarProps {
  name: string | null | undefined;
  /** Sizing/typography via utility classes, e.g. "h-24 w-24 text-2xl". */
  className?: string;
  /** Decorative when the name is shown next to it (the hero); default true. */
  decorative?: boolean;
  /**
   * "surface" (default): a subtle surface fill with accent initials. "accent":
   * a solid accent fill with accent-text initials, for placements whose
   * background is the surface colour — e.g. the portfolio header, where the
   * default Header color equals the surface (EPD-12191).
   */
  tone?: "surface" | "accent";
}

const TONES = {
  surface: {
    background: "var(--pf-surface, var(--muted))",
    color: "var(--pf-accent, var(--primary))",
  },
  accent: {
    background: "var(--pf-accent, var(--primary))",
    color: "var(--pf-accent-text, var(--primary-foreground))",
  },
} as const;

export function InitialsAvatar({
  name,
  className,
  decorative = true,
  tone = "surface",
}: InitialsAvatarProps) {
  const initials = getInitials(name ?? "");

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full font-semibold",
        className,
      )}
      style={{
        ...TONES[tone],
        fontFamily: "var(--pf-font-heading, inherit)",
      }}
      data-tone={tone}
      aria-hidden={decorative || undefined}
      aria-label={!decorative && name ? name : undefined}
      role={!decorative && name ? "img" : undefined}
    >
      {initials || <User className="h-1/2 w-1/2 opacity-60" aria-hidden />}
    </div>
  );
}
